import React, { useEffect, useState } from "react";
import { Star, Users, ShieldCheck } from "lucide-react";
import { useProof } from "@/lib/proof";
import { getLiveStats } from "@/lib/api";
import { Container, CountUp } from "@/components/ui-kit";
import { cn } from "@/lib/utils";

const Stars = ({ score }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${score} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={cn("h-3.5 w-3.5", i <= Math.round(score) ? "fill-mk-lime text-mk-lime" : "text-mk-strong")} />)}
  </span>
);

const timeAgo = (iso) => {
  if (!iso) return null;
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export const ProofStrip = () => {
  const { items: ratings } = useProof("ratings");
  const { items: badges } = useProof("badges");
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => getLiveStats().then((d) => alive && setStats(d)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="border-y border-mk-border bg-mk-surface/60 backdrop-blur" data-testid="proof-strip">
      <Container className="py-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
          <div className="flex items-center gap-3 shrink-0" data-testid="proof-live-counter">
            <span className="grid place-items-center h-9 w-9 rounded-xl border border-mk-lime/40 bg-mk-lime/10 text-mk-lime"><Users className="h-4 w-4" /></span>
            <div className="leading-tight">
              <div className="flex items-center gap-2 text-sm text-mk-text">
                <span className="live-dot" />
                <span className="font-display font-bold text-lg text-mk-lime tabular-nums" data-testid="proof-onboarded-count">
                  {stats ? <CountUp value={stats.onboarded_this_month} duration={1.2} /> : "—"}
                </span>
                <span>customers onboarded in <span className="text-mk-text font-medium">{stats?.month || "this month"}</span></span>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-mk-muted" data-testid="proof-last-onboarded">
                {stats?.workspaces_total ? `${stats.workspaces_total} live workspaces` : "Live from fixlr.app"}{stats?.last_onboarded_at ? ` · latest ${timeAgo(stats.last_onboarded_at)}` : ""}
              </div>
            </div>
          </div>

          {ratings.length > 0 && <div className="hidden lg:block h-8 w-px bg-mk-border" />}

          {ratings.length > 0 && <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="proof-ratings">
            {ratings.map((r) => (
              <a key={r.source} href={r.url} target="_blank" rel="noreferrer" className="group flex items-center gap-2.5" data-testid={`proof-rating-${r.source.toLowerCase()}`}>
                <span className={cn("grid place-items-center h-8 w-8 rounded-lg border text-[11px] font-bold", r.tone === "blue" ? "border-mk-blue/50 bg-mk-blue/15 text-[#7fb0ff]" : "border-mk-lime/40 bg-mk-lime/10 text-mk-lime")}>{r.source[0]}</span>
                <span className="leading-tight">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-mk-text"><span className="tabular-nums">{Number(r.score).toFixed(1)}</span><Stars score={Number(r.score)} /></span>
                  <span className="block text-[11px] text-mk-muted group-hover:text-mk-dim transition-colors">{r.source}{r.count ? ` · ${r.count} reviews` : ""}</span>
                </span>
              </a>
            ))}
          </div>}

          {badges.length > 0 && <div className="lg:ml-auto flex flex-wrap items-center gap-2" data-testid="proof-badges">
            {badges.map((b, i) => (
              <span key={b.label || b} className={cn("inline-flex items-center gap-1.5 rounded-full border border-mk-border bg-mk-bg px-2.5 py-1 text-[11px] text-mk-dim", i >= 2 && "lg:hidden 2xl:inline-flex")}><ShieldCheck className="h-3 w-3 text-mk-lime" />{b.label || b}</span>
            ))}
          </div>}
        </div>
      </Container>
    </div>
  );
};

export default ProofStrip;
